import React, { useEffect, useState } from 'react';
import { optimizedApi } from '@/utils/api/optimized-api';
import { showToast } from '@/components/ui/Toast';
import InputField from '@/components/common/InputField';
import Modal from '@/components/Modal/Modal';
import { Loader2, Plus, Trash2, ChevronDown } from 'lucide-react';
import { bulkAssignLeads, getLeadDetails, updateLead } from '@/actions/lead-manager';
import { ColumnsService } from '@/utils/columnsCache';
import { Column } from '@/types/columns';
import { Lead } from '@/types/leads/Table';
import {
    LeadProfileDetails,
    LeadProfileResponse,
    LeadProfileSectionKey,
    LeadSchoolHistoryEntry,
    LeadReferenceEntry,
    LeadCommunicationLogEntry,
    LEAD_SOURCE_SUBCATEGORY_MAP,
    CONSIDERATION_OPTIONS,
    STAGE_OF_ADMISSION_STEPS,
    QUALIFIED_FROM_STAGE,
    OVERALL_STATUS_OPTIONS,
    COUNSELLOR_PRIORITY_OPTIONS,
    COUNSELLOR_QUALIFICATION_OPTIONS,
    CURRICULUM_OPTIONS,
    GENDER_OPTIONS,
    DISQUALIFIED_REASONS,
    CLOSED_DEAD_REASONS,
    BRAND_AWARENESS_OPTIONS,
    ACADEMIC_YEAR_OPTIONS,
    IS_CONTACTABLE_OPTIONS,
    PROFILE_FIELD_LABELS,
} from '@/types/leads/Profile';

interface AssignableUser {
    value: string;
    label: string;
}

interface LeadProfileSectionsProps {
    leadUuid: string;
    lead: Lead;
    sectionsEnabled: LeadProfileSectionKey[];
    dependentSourceEnabled: boolean;
    activeUsersForDropdown?: AssignableUser[];
}

const SECTION_META: Record<LeadProfileSectionKey, string> = {
    academic: 'Student',
    guardian: 'Guardian',
    residence: 'Residence',
    school_history: 'School History',
    references: 'References',
    brand_awareness: 'Brand Awareness',
    communication: 'Communication History',
    qualification_status: 'Qualification & Status',
};

// Child Details bundles these sub-sections under one top-level tab, matching the
// source spec's "Child Details -> Student / Guardian / School History / References / ..." grouping.
const CHILD_DETAIL_KEYS: LeadProfileSectionKey[] = ['academic', 'guardian', 'residence', 'school_history', 'references', 'brand_awareness'];

// The 'communication' section powers two tabs: Lead Source (where the lead came from) and
// Communication History (contacting the parent) — kept apart because one combined tab
// confused counsellors.
type TopLevelGroupKey = 'general' | 'lead_source' | 'child_details' | 'communication' | 'qualification_status';
const GROUP_LABELS: Record<TopLevelGroupKey, string> = {
    general: 'General',
    lead_source: 'Lead Source',
    child_details: 'Child Details',
    communication: 'Communication History',
    qualification_status: 'Qualification & Status',
};

const asOpts = (values: string[]) => values.map((v) => ({ value: v, label: v }));
const yesNo = [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];

const emptySchoolEntry = (sequence: 1 | 2): LeadSchoolHistoryEntry => ({ sequence });
const emptyReferenceEntry = (sequence: 1 | 2): LeadReferenceEntry => ({ sequence });

const LeadProfileSections: React.FC<LeadProfileSectionsProps> = ({ leadUuid, lead, sectionsEnabled, dependentSourceEnabled, activeUsersForDropdown = [] }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const visibleChildTabs = CHILD_DETAIL_KEYS.filter((k) => sectionsEnabled.includes(k));
    // General (Lead ID, Counsellor Assigned) is always shown, first — per the source spec these
    // are required/system-level fields, not an optional configurable section.
    const visibleGroups: TopLevelGroupKey[] = [
        'general',
        ...(sectionsEnabled.includes('communication') ? (['lead_source'] as const) : []),
        ...(visibleChildTabs.length > 0 ? (['child_details'] as const) : []),
        ...(sectionsEnabled.includes('communication') ? (['communication'] as const) : []),
        ...(sectionsEnabled.includes('qualification_status') ? (['qualification_status'] as const) : []),
    ];

    const [activeGroup, setActiveGroup] = useState<TopLevelGroupKey>(visibleGroups[0]);
    const [activeChildTab, setActiveChildTab] = useState<LeadProfileSectionKey>(visibleChildTabs[0]);
    const activeSection: LeadProfileSectionKey | 'general' | 'lead_source' = activeGroup === 'child_details' ? activeChildTab : activeGroup;

    // Counsellor Assigned reuses the existing assignment system (crm_leads_assignments / bulkAssignLeads) —
    // not a crm_lead_details column — so it has its own state and saves immediately on change.
    const currentAssignment = ((lead as any)?.assignments || []).find((a: any) => a.is_active);
    const [ownerUuid, setOwnerUuid] = useState<string>(currentAssignment?.user?.uuid || '');
    const [assigningOwner, setAssigningOwner] = useState(false);

    // Grade and contact details are native crm_leads fields, edited in place here through the
    // same endpoints the native Lead Form tab uses — not duplicated into crm_lead_details.
    const [nativeColumns, setNativeColumns] = useState<Column[]>([]);
    const [gradeUuid, setGradeUuid] = useState<string>((lead as any)?.grade_uuid || '');
    const [savingGrade, setSavingGrade] = useState(false);
    const [pendingGradeUuid, setPendingGradeUuid] = useState<string | null>(null);
    const [gradeRemark, setGradeRemark] = useState('');

    // Alternative Phone is a client custom column (crm_leads_custom_fields), not a crm_leads field.
    const altPhoneOf = (l: any): string => {
        const formatted = l?.formattedCustomInfo?.alternative_phone;
        if (formatted?.value) return String(formatted.value);
        const info = (l?.customInfo || []).find((c: any) => c.column?.col_name === 'alternative_phone');
        if (info?.custom_field_value) return info.custom_field_value;
        const field = (l?.customFields || []).find((c: any) => c.column?.col_name === 'alternative_phone');
        return field?.value_text || '';
    };
    const initialAltPhone = altPhoneOf(lead);
    const [contactName, setContactName] = useState<string>(lead?.name || '');
    const [contactPhone, setContactPhone] = useState<string>(lead?.phone || '');
    const [contactEmail, setContactEmail] = useState<string>(lead?.email || '');
    const [altPhone, setAltPhone] = useState<string>(initialAltPhone);
    const [pendingContactName, setPendingContactName] = useState<string | null>(null);
    const [pendingContactPhone, setPendingContactPhone] = useState<string | null>(null);
    const [pendingContactEmail, setPendingContactEmail] = useState<string | null>(null);
    const [pendingAltPhone, setPendingAltPhone] = useState<string | null>(null);
    const [contactRemark, setContactRemark] = useState('');
    const [savingContact, setSavingContact] = useState(false);

    useEffect(() => {
        ColumnsService.getUserColumns().then((res) => {
            if (res.success) setNativeColumns(res.columns);
        });
    }, []);

    // The lead passed in is the leads-table row, which can be stale after an edit here — load the
    // saved values, as the Yellowslate Form does, so both forms always show the same thing.
    useEffect(() => {
        let cancelled = false;
        getLeadDetails(leadUuid).then((res: any) => {
            if (cancelled || !res?.success || !res.data) return;
            const d = res.data;
            setContactName(d.name || '');
            setContactPhone(d.phone || '');
            setContactEmail(d.email || '');
            setAltPhone(altPhoneOf(d));
            setGradeUuid(d.grade_uuid || d.gradeOption?.uuid || '');
        }).catch(() => { /* fall back to the row's values */ });
        return () => { cancelled = true; };
    }, [leadUuid]);

    const gradeColumn = nativeColumns.find((c) => c.col_name === 'grade');
    const nameColumn = nativeColumns.find((c) => c.col_name === 'name');
    const phoneColumn = nativeColumns.find((c) => c.col_name === 'phone');
    const emailColumn = nativeColumns.find((c) => c.col_name === 'email');
    const altPhoneColumn = nativeColumns.find((c) => c.col_name === 'alternative_phone');

    const contactChanged =
        (pendingContactName !== null && pendingContactName !== contactName) ||
        (pendingContactPhone !== null && pendingContactPhone !== contactPhone) ||
        (pendingContactEmail !== null && pendingContactEmail !== contactEmail) ||
        (pendingAltPhone !== null && pendingAltPhone !== altPhone);

    const confirmContactChange = async (): Promise<boolean> => {
        if (!contactRemark.trim()) {
            showToast('A short reason is required to change contact details', 'error');
            return false;
        }
        if (pendingContactName !== null && pendingContactName !== contactName && !pendingContactName.trim()) {
            showToast('Contact Name cannot be empty', 'error');
            return false;
        }
        const changes: Record<string, string> = {};
        if (pendingContactName !== null && pendingContactName !== contactName && nameColumn) {
            changes[nameColumn.uuid] = pendingContactName.trim();
        }
        if (pendingContactPhone !== null && pendingContactPhone !== contactPhone && phoneColumn) {
            changes[phoneColumn.uuid] = pendingContactPhone;
        }
        if (pendingContactEmail !== null && pendingContactEmail !== contactEmail && emailColumn) {
            changes[emailColumn.uuid] = pendingContactEmail;
        }
        if (pendingAltPhone !== null && pendingAltPhone !== altPhone && altPhoneColumn) {
            changes[altPhoneColumn.uuid] = pendingAltPhone.trim();
        }
        if (Object.keys(changes).length === 0) return true;

        setSavingContact(true);
        try {
            const res: any = await updateLead(leadUuid, changes, contactRemark.trim(), undefined, undefined, undefined, true);
            if (res.success) {
                if (pendingContactName !== null) setContactName(pendingContactName.trim());
                if (pendingContactPhone !== null) setContactPhone(pendingContactPhone);
                if (pendingContactEmail !== null) setContactEmail(pendingContactEmail);
                if (pendingAltPhone !== null) setAltPhone(pendingAltPhone.trim());
                setPendingContactName(null);
                setPendingContactPhone(null);
                setPendingContactEmail(null);
                setPendingAltPhone(null);
                setContactRemark('');
                showToast('Contact details updated', 'success');
                return true;
            }
            showToast(res.message || 'Failed to update contact details', 'error');
        } catch {
            showToast('Failed to update contact details', 'error');
        } finally {
            setSavingContact(false);
        }
        return false;
    };

    const confirmGradeChange = async (): Promise<boolean> => {
        if (!pendingGradeUuid || !gradeColumn) return true;
        if (!gradeRemark.trim()) {
            showToast('A short reason is required to change Grade', 'error');
            return false;
        }
        setSavingGrade(true);
        try {
            const res: any = await updateLead(leadUuid, { [gradeColumn.uuid]: pendingGradeUuid }, gradeRemark.trim(), undefined, undefined, undefined, true);
            if (res.success) {
                setGradeUuid(pendingGradeUuid);
                setPendingGradeUuid(null);
                setGradeRemark('');
                showToast('Grade updated', 'success');
                return true;
            }
            showToast(res.message || 'Failed to update grade', 'error');
        } catch {
            showToast('Failed to update grade', 'error');
        } finally {
            setSavingGrade(false);
        }
        return false;
    };

    const [form, setForm] = useState<LeadProfileDetails>({});
    const [schoolHistory, setSchoolHistory] = useState<Record<1 | 2, LeadSchoolHistoryEntry>>({
        1: emptySchoolEntry(1),
        2: emptySchoolEntry(2),
    });
    const [references, setReferences] = useState<Record<1 | 2, LeadReferenceEntry>>({
        1: emptyReferenceEntry(1),
        2: emptyReferenceEntry(2),
    });
    const [commLog, setCommLog] = useState<LeadCommunicationLogEntry[]>([]);
    const [summaryLastUpdate, setSummaryLastUpdate] = useState<{ at: string; by: string | null } | null>(null);
    const [savedSummary, setSavedSummary] = useState<string>('');
    const [summaryHistory, setSummaryHistory] = useState<{ id: number; at: string; by: string | null; text: string }[]>([]);
    const [summaryHistoryOpen, setSummaryHistoryOpen] = useState(false);
    const [newLogNote, setNewLogNote] = useState('');
    const [newLogChannel, setNewLogChannel] = useState('call');
    const [addingLog, setAddingLog] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        optimizedApi.get<LeadProfileResponse>(`/lead-profile/${leadUuid}`).then((res) => {
            if (cancelled) return;
            if (res.success && res.data) {
                setForm(res.data.details || {});
                const sh: Record<1 | 2, LeadSchoolHistoryEntry> = { 1: emptySchoolEntry(1), 2: emptySchoolEntry(2) };
                (res.data.schoolHistory || []).forEach((e) => { sh[e.sequence] = e; });
                setSchoolHistory(sh);
                const refs: Record<1 | 2, LeadReferenceEntry> = { 1: emptyReferenceEntry(1), 2: emptyReferenceEntry(2) };
                (res.data.references || []).forEach((e) => { refs[e.sequence] = e; });
                setReferences(refs);
                setCommLog(res.data.recentCommunicationLog || []);
                setSummaryLastUpdate(res.data.summaryNotesLastUpdate || null);
                setSummaryHistory(res.data.summaryNotesHistory || []);
                setSavedSummary(res.data.details?.summary_notes || '');
            }
            setLoading(false);
        }).catch(() => setLoading(false));
        return () => { cancelled = true; };
    }, [leadUuid]);

    const setField = (key: keyof LeadProfileDetails, value: any) => setForm((f) => ({ ...f, [key]: value }));

    // Stage of Admission drives two fields, and the counsellor is told about each change:
    //  - Walk-in planned/setup or any later stage sets Counsellor Qualification to Qualified.
    //  - Admitted also sets Qualified Lead Status to Admitted.
    const [stageNotices, setStageNotices] = useState<string[]>([]);
    const setStageOfAdmission = (value: string) => {
        const changes: Partial<LeadProfileDetails> = { stage_of_admission: value };
        const notices: string[] = [];
        const stageIdx = STAGE_OF_ADMISSION_STEPS.indexOf(value);
        if (stageIdx >= STAGE_OF_ADMISSION_STEPS.indexOf(QUALIFIED_FROM_STAGE) && form.counsellor_qualification !== 'Qualified') {
            changes.counsellor_qualification = 'Qualified';
            notices.push('qualified');
        }
        if (value === 'Admitted' && form.overall_status !== 'Admitted') {
            changes.overall_status = 'Admitted';
            notices.push('admitted');
        }
        setForm((f) => ({ ...f, ...changes }));
        if (notices.length) setStageNotices(notices);
    };

    const saveDetails = async () => {
        // Grade and contact fields have their own reason + Confirm; Save Changes commits any still
        // pending too, so a picked value is never left looking saved when it isn't.
        const gradePending = !!pendingGradeUuid && pendingGradeUuid !== gradeUuid;
        if ((gradePending && !gradeRemark.trim()) || (contactChanged && !contactRemark.trim())) {
            showToast('Enter a reason for the Grade / contact change before saving', 'error');
            return;
        }
        if (gradePending && !(await confirmGradeChange())) return;
        if (contactChanged && !(await confirmContactChange())) return;
        setSaving(true);
        const res = await optimizedApi.put<LeadProfileDetails>(`/lead-profile/${leadUuid}`, form);
        setSaving(false);
        if (res.success) {
            showToast('Profile saved', 'success');
            if (res.data) setForm(res.data);
            const newSummary = (res.data?.summary_notes ?? form.summary_notes) || '';
            if (newSummary.trim() !== savedSummary.trim()) {
                let by: string | null = null;
                try {
                    const user = JSON.parse(localStorage.getItem('crm_user') || 'null');
                    const u = user?.data?.data || user?.data || user;
                    by = u?.userName || u?.name || null;
                } catch { /* name is optional */ }
                const at = new Date().toISOString();
                setSummaryLastUpdate({ at, by });
                setSummaryHistory((h) => [{ id: Date.now(), at, by, text: newSummary }, ...h]);
                setSavedSummary(newSummary);
            }
        } else {
            const msg = (res as any).errors?.join('; ') || res.message || 'Failed to save profile';
            showToast(msg, 'error');
        }
    };

    const saveSchoolHistory = async (sequence: 1 | 2) => {
        const entry = schoolHistory[sequence];
        const res = await optimizedApi.post<LeadSchoolHistoryEntry>(`/lead-profile/${leadUuid}/school-history`, { ...entry, sequence });
        if (res.success && res.data) {
            setSchoolHistory((s) => ({ ...s, [sequence]: res.data! }));
            showToast(`School History ${sequence} saved`, 'success');
        } else {
            showToast(res.message || 'Failed to save school history', 'error');
        }
    };

    const removeSchoolHistory = async (sequence: 1 | 2) => {
        const res = await optimizedApi.delete(`/lead-profile/${leadUuid}/school-history/${sequence}`);
        if (res.success) {
            setSchoolHistory((s) => ({ ...s, [sequence]: emptySchoolEntry(sequence) }));
            showToast(`School History ${sequence} removed`, 'success');
        } else {
            showToast(res.message || 'Failed to remove school history', 'error');
        }
    };

    const saveReference = async (sequence: 1 | 2) => {
        const entry = references[sequence];
        const res = await optimizedApi.post<LeadReferenceEntry>(`/lead-profile/${leadUuid}/references`, { ...entry, sequence });
        if (res.success && res.data) {
            setReferences((r) => ({ ...r, [sequence]: res.data! }));
            showToast(`Reference ${sequence} saved`, 'success');
        } else {
            showToast(res.message || 'Failed to save reference', 'error');
        }
    };

    const removeReference = async (sequence: 1 | 2) => {
        const res = await optimizedApi.delete(`/lead-profile/${leadUuid}/references/${sequence}`);
        if (res.success) {
            setReferences((r) => ({ ...r, [sequence]: emptyReferenceEntry(sequence) }));
            showToast(`Reference ${sequence} removed`, 'success');
        } else {
            showToast(res.message || 'Failed to remove reference', 'error');
        }
    };

    const addLogEntry = async () => {
        if (!newLogNote.trim()) {
            showToast('Enter a note before adding to the log', 'error');
            return;
        }
        setAddingLog(true);
        const res = await optimizedApi.post<LeadCommunicationLogEntry>(`/lead-profile/${leadUuid}/communication-log`, {
            channel: newLogChannel,
            note: newLogNote,
        });
        setAddingLog(false);
        if (res.success && res.data) {
            setCommLog((log) => [res.data!, ...log]);
            setNewLogNote('');
            showToast('Log entry added', 'success');
        } else {
            showToast(res.message || 'Failed to add log entry', 'error');
        }
    };

    const handleOwnerChange = async (newOwnerUuid: string) => {
        setAssigningOwner(true);
        try {
            const res: any = await bulkAssignLeads([leadUuid], newOwnerUuid, '');
            if (res.success) {
                setOwnerUuid(newOwnerUuid);
                showToast('Counsellor assigned', 'success');
            } else {
                showToast(res.message || 'Failed to assign counsellor', 'error');
            }
        } catch {
            showToast('Failed to assign counsellor', 'error');
        } finally {
            setAssigningOwner(false);
        }
    };

    const toggleConsideration = (value: string) => {
        setForm((f) => {
            const current = [f.consideration_1, f.consideration_2].filter(Boolean) as string[];
            if (current.includes(value)) {
                const next = current.filter((v) => v !== value);
                return { ...f, consideration_1: next[0] || null, consideration_2: next[1] || null };
            }
            if (current.length >= 2) {
                showToast('Only 2 considerations can be selected — remove one first', 'error');
                return f;
            }
            const next = [...current, value];
            return { ...f, consideration_1: next[0] || null, consideration_2: next[1] || null };
        });
    };

    const subcategoryOptions = form.lead_source_category && dependentSourceEnabled
        ? (LEAD_SOURCE_SUBCATEGORY_MAP[form.lead_source_category] || [])
        : Array.from(new Set(Object.values(LEAD_SOURCE_SUBCATEGORY_MAP).flat()));

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading profile…
            </div>
        );
    }


    return (
        <div className="pt-4 px-4 max-w-3xl min-h-full flex flex-col">
            {/* Top-level tabs: General / Lead Source / Child Details / Communication History / Qualification & Status */}
            <div className="border-b border-gray-200 mb-6 shrink-0">
                <nav className="flex gap-6 -mb-px overflow-x-auto">
                    {visibleGroups.map((group) => (
                        <button
                            key={group}
                            onClick={() => setActiveGroup(group)}
                            className={`whitespace-nowrap pb-3 text-sm border-b-2 transition-colors ${
                                activeGroup === group
                                    ? 'border-brandColor text-brandColor font-semibold'
                                    : 'border-transparent text-gray-500 font-medium hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {GROUP_LABELS[group]}
                        </button>
                    ))}
                </nav>
            </div>

            <div className={activeGroup === 'child_details' ? 'flex gap-8 flex-1' : 'flex-1'}>
                {/* Vertical sub-nav for Child Details: Student / Guardian / Residence / School History / References / Brand Awareness */}
                {activeGroup === 'child_details' && (
                    <nav className="flex flex-col gap-0.5 w-44 shrink-0 pr-4 border-r border-gray-200">
                        {visibleChildTabs.map((key) => (
                            <button
                                key={key}
                                onClick={() => setActiveChildTab(key)}
                                className={`text-left px-3 py-2 rounded-r-md text-sm font-medium transition-colors border-l-2 ${
                                    activeChildTab === key
                                        ? 'border-brandColor bg-gray-50 text-brandColor font-semibold'
                                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                }`}
                            >
                                {SECTION_META[key]}
                            </button>
                        ))}
                    </nav>
                )}

                <div className="flex-1 min-w-0">

            {activeSection === 'general' && (
                <div className="space-y-5 max-w-lg">
                    <InputField id="lead_id" name="lead_id" type="text" label="Lead ID" disabled
                        value={lead?.uuid || ''} onChange={() => {}} />

                    <div>
                        <InputField id="counsellor_assigned" name="counsellor_assigned" type="select" label="Counsellor Assigned"
                            value={ownerUuid} disabled={assigningOwner}
                            options={[{ value: '', label: 'Yet to assign' }, ...activeUsersForDropdown]}
                            onChange={(e) => handleOwnerChange(e.target.value)} />
                        <p className="text-xs text-gray-400 mt-1">Saves immediately.</p>
                    </div>
                </div>
            )}

            {activeSection === 'lead_source' && (
                <div className="grid grid-cols-2 gap-4">
                    <InputField id="lead_created_date" name="lead_created_date" type="text" label="Lead Created Date" disabled
                        value={(lead as any)?.created_date ? new Date((lead as any).created_date).toLocaleString() : ''} onChange={() => {}} />
                    <div />
                    <InputField id="lead_source_category" name="lead_source_category" type="select" label={PROFILE_FIELD_LABELS.lead_source_category}
                        value={form.lead_source_category || ''} options={asOpts(Object.keys(LEAD_SOURCE_SUBCATEGORY_MAP))}
                        onChange={(e) => setForm((f) => ({ ...f, lead_source_category: e.target.value, lead_source_subcategory: null }))} />
                    <InputField id="lead_source_subcategory" name="lead_source_subcategory" type="select" label={PROFILE_FIELD_LABELS.lead_source_subcategory}
                        value={form.lead_source_subcategory || ''} options={asOpts(subcategoryOptions)}
                        onChange={(e) => setField('lead_source_subcategory', e.target.value)} />
                </div>
            )}

            {activeSection === 'academic' && (
                <div className="grid grid-cols-2 gap-4">
                    <InputField id="academic_year_joining" name="academic_year_joining" type="select" label={PROFILE_FIELD_LABELS.academic_year_joining}
                        value={form.academic_year_joining || ''}
                        options={asOpts(form.academic_year_joining && !ACADEMIC_YEAR_OPTIONS.includes(form.academic_year_joining)
                            ? [form.academic_year_joining, ...ACADEMIC_YEAR_OPTIONS]
                            : ACADEMIC_YEAR_OPTIONS)}
                        onChange={(e) => setField('academic_year_joining', e.target.value)} />
                    <InputField id="curriculum" name="curriculum" type="select" label="Curriculum"
                        value={form.curriculum || ''} options={asOpts(CURRICULUM_OPTIONS)}
                        onChange={(e) => setField('curriculum', e.target.value)} />
                    <InputField id="student_first_name" name="student_first_name" type="text" label="Student First Name"
                        value={form.student_first_name || ''} onChange={(e) => setField('student_first_name', e.target.value)} />
                    <InputField id="student_last_name" name="student_last_name" type="text" label="Student Last Name"
                        value={form.student_last_name || ''} onChange={(e) => setField('student_last_name', e.target.value)} />
                    <div>
                        <InputField id="grade" name="grade" type="select" label="Grade" disabled={savingGrade || !gradeColumn}
                            value={pendingGradeUuid ?? gradeUuid}
                            options={(gradeColumn?.options || []).slice().sort((a, b) => a.opt_order - b.opt_order).map((o) => ({ value: o.uuid, label: o.opt_name }))}
                            onChange={(e) => setPendingGradeUuid(e.target.value)} />
                        {pendingGradeUuid && pendingGradeUuid !== gradeUuid && (
                            <div className="mt-2 flex gap-2">
                                <input value={gradeRemark} onChange={(e) => setGradeRemark(e.target.value)}
                                    placeholder="Reason for change (required)"
                                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs" />
                                <button onClick={confirmGradeChange} disabled={savingGrade}
                                    className="px-2.5 py-1.5 bg-brandColor text-white rounded-md text-xs font-semibold disabled:opacity-50">
                                    {savingGrade ? 'Saving…' : 'Confirm'}
                                </button>
                            </div>
                        )}
                    </div>
                    <InputField id="contact_name" name="contact_name" type="text" label="Contact Name" disabled={savingContact || !nameColumn}
                        value={pendingContactName ?? contactName} onChange={(e) => setPendingContactName(e.target.value)} />
                    <InputField id="contact_phone" name="contact_phone" type="text" label="Contact Phone" disabled={savingContact || !phoneColumn}
                        value={pendingContactPhone ?? contactPhone} onChange={(e) => setPendingContactPhone(e.target.value)} />
                    {altPhoneColumn && (
                        <InputField id="alternative_phone" name="alternative_phone" type="text" label={altPhoneColumn.col_label || 'Alternative Phone'} disabled={savingContact}
                            value={pendingAltPhone ?? altPhone} onChange={(e) => setPendingAltPhone(e.target.value)} />
                    )}
                    <InputField id="contact_email" name="contact_email" type="text" label="Contact Email" disabled={savingContact || !emailColumn}
                        value={pendingContactEmail ?? contactEmail} onChange={(e) => setPendingContactEmail(e.target.value)} />
                    {contactChanged && (
                        <div className="col-span-2 -mt-2 flex gap-2">
                            <input value={contactRemark} onChange={(e) => setContactRemark(e.target.value)}
                                placeholder="Reason for change (required)"
                                className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs" />
                            <button onClick={confirmContactChange} disabled={savingContact}
                                className="px-2.5 py-1.5 bg-brandColor text-white rounded-md text-xs font-semibold disabled:opacity-50">
                                {savingContact ? 'Saving…' : 'Confirm'}
                            </button>
                        </div>
                    )}
                    <InputField id="date_of_birth" name="date_of_birth" type="date" label="Date of Birth"
                        value={form.date_of_birth || ''} onChange={(e) => setField('date_of_birth', e.target.value)} />
                    <InputField id="gender" name="gender" type="select" label="Gender"
                        value={form.gender || ''} options={asOpts(GENDER_OPTIONS)}
                        onChange={(e) => setField('gender', e.target.value)} />
                </div>
            )}

            {activeSection === 'guardian' && (
                <div className="grid grid-cols-2 gap-4">
                    <InputField id="is_existing_parent" name="is_existing_parent" type="select" label="Is Existing Parent"
                        value={form.is_existing_parent === null || form.is_existing_parent === undefined ? '' : String(form.is_existing_parent)}
                        options={yesNo} onChange={(e) => setField('is_existing_parent', e.target.value === 'true')} />
                    <div />
                    <InputField id="father_name" name="father_name" type="text" label="Father Name"
                        value={form.father_name || ''} onChange={(e) => setField('father_name', e.target.value)} />
                    <InputField id="father_occupation" name="father_occupation" type="text" label="Father Occupation"
                        value={form.father_occupation || ''} onChange={(e) => setField('father_occupation', e.target.value)} />
                    <InputField id="father_company" name="father_company" type="text" label="Father Company / Organization"
                        value={form.father_company || ''} onChange={(e) => setField('father_company', e.target.value)} />
                    <InputField id="father_qualification" name="father_qualification" type="text" label="Father Qualification / Education"
                        value={form.father_qualification || ''} onChange={(e) => setField('father_qualification', e.target.value)} />
                    <InputField id="mother_name" name="mother_name" type="text" label="Mother Name"
                        value={form.mother_name || ''} onChange={(e) => setField('mother_name', e.target.value)} />
                    <InputField id="mother_occupation" name="mother_occupation" type="text" label="Mother Occupation"
                        value={form.mother_occupation || ''} onChange={(e) => setField('mother_occupation', e.target.value)} />
                    <InputField id="mother_company" name="mother_company" type="text" label="Mother Company / Organization"
                        value={form.mother_company || ''} onChange={(e) => setField('mother_company', e.target.value)} />
                    <InputField id="mother_qualification" name="mother_qualification" type="text" label="Mother Qualification / Education"
                        value={form.mother_qualification || ''} onChange={(e) => setField('mother_qualification', e.target.value)} />
                </div>
            )}

            {activeSection === 'residence' && (
                <div className="grid grid-cols-2 gap-4">
                    <InputField id="current_city" name="current_city" type="text" label="Current City of Residence"
                        value={form.current_city || ''} onChange={(e) => setField('current_city', e.target.value)} />
                    <InputField id="current_country" name="current_country" type="text" label="Current Country of Residence"
                        value={form.current_country || ''} onChange={(e) => setField('current_country', e.target.value)} />
                    <InputField id="is_local" name="is_local" type="select" label="Is Local"
                        value={form.is_local === null || form.is_local === undefined ? '' : String(form.is_local)}
                        options={yesNo} onChange={(e) => setField('is_local', e.target.value === 'true')} />
                    <div />
                    <div className="col-span-2">
                        <InputField id="residential_address" name="residential_address" type="textarea" label="Residential Address" rows={3}
                            value={form.residential_address || ''} onChange={(e) => setField('residential_address', e.target.value)} />
                    </div>
                </div>
            )}

            {activeSection === 'brand_awareness' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">How did you come to know about us?</label>
                    <div className="flex flex-col gap-2">
                        {BRAND_AWARENESS_OPTIONS.map((option) => {
                            const checked = (form.brand_awareness || []).includes(option);
                            return (
                                <label key={option} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                            setForm((f) => {
                                                const current = f.brand_awareness || [];
                                                const next = current.includes(option) ? current.filter((v) => v !== option) : [...current, option];
                                                return { ...f, brand_awareness: next };
                                            });
                                        }}
                                        className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
                                    />
                                    {option}
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}

            {activeSection === 'school_history' && (
                <div className="space-y-4">
                    {([1, 2] as const).map((seq) => (
                        <div key={seq} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-sm text-gray-700">School History {seq}</h4>
                                {schoolHistory[seq].school_history_id && (
                                    <button onClick={() => removeSchoolHistory(seq)} className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1">
                                        <Trash2 className="h-3.5 w-3.5" /> Remove
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <InputField id={`school_name_${seq}`} name={`school_name_${seq}`} type="text" label="Name"
                                    value={schoolHistory[seq].school_name || ''}
                                    onChange={(e) => setSchoolHistory((s) => ({ ...s, [seq]: { ...s[seq], school_name: e.target.value } }))} />
                                <InputField id={`school_city_${seq}`} name={`school_city_${seq}`} type="text" label="City"
                                    value={schoolHistory[seq].city || ''}
                                    onChange={(e) => setSchoolHistory((s) => ({ ...s, [seq]: { ...s[seq], city: e.target.value } }))} />
                                <InputField id={`school_board_${seq}`} name={`school_board_${seq}`} type="text" label="Board" placeholder="e.g. CBSE, ICSE, IB, State board"
                                    value={schoolHistory[seq].board || ''}
                                    onChange={(e) => setSchoolHistory((s) => ({ ...s, [seq]: { ...s[seq], board: e.target.value } }))} />
                                <InputField id={`school_year_${seq}`} name={`school_year_${seq}`} type="number" label="Year of Leaving"
                                    value={schoolHistory[seq].year_of_leaving ?? ''}
                                    onChange={(e) => setSchoolHistory((s) => ({ ...s, [seq]: { ...s[seq], year_of_leaving: e.target.value ? Number(e.target.value) : null } }))} />
                                <div className="col-span-2">
                                    <InputField id={`school_reason_${seq}`} name={`school_reason_${seq}`} type="text" label="Reason for Leaving"
                                        value={schoolHistory[seq].reason_for_leaving || ''}
                                        onChange={(e) => setSchoolHistory((s) => ({ ...s, [seq]: { ...s[seq], reason_for_leaving: e.target.value } }))} />
                                </div>
                            </div>
                            <button onClick={() => saveSchoolHistory(seq)} className="text-xs font-semibold text-brandColor hover:underline">
                                Save School {seq}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {activeSection === 'references' && (
                <div className="space-y-4">
                    {([1, 2] as const).map((seq) => (
                        <div key={seq} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-sm text-gray-700">Reference {seq}</h4>
                                {references[seq].reference_id && (
                                    <button onClick={() => removeReference(seq)} className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1">
                                        <Trash2 className="h-3.5 w-3.5" /> Remove
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-3 gap-3 mb-3">
                                <InputField id={`ref_name_${seq}`} name={`ref_name_${seq}`} type="text" label="Name"
                                    value={references[seq].name || ''}
                                    onChange={(e) => setReferences((r) => ({ ...r, [seq]: { ...r[seq], name: e.target.value } }))} />
                                <InputField id={`ref_phone_${seq}`} name={`ref_phone_${seq}`} type="tel" label="Contact Number"
                                    value={references[seq].contact_number || ''}
                                    onChange={(e) => setReferences((r) => ({ ...r, [seq]: { ...r[seq], contact_number: e.target.value } }))} />
                                <InputField id={`ref_rel_${seq}`} name={`ref_rel_${seq}`} type="select" label="Relationship"
                                    value={references[seq].relationship || ''} options={asOpts(['Friend', 'Relative', 'Colleague', 'Others'])}
                                    onChange={(e) => setReferences((r) => ({ ...r, [seq]: { ...r[seq], relationship: e.target.value } }))} />
                            </div>
                            <button onClick={() => saveReference(seq)} className="text-xs font-semibold text-brandColor hover:underline">
                                Save Reference {seq}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {activeSection === 'communication' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <InputField id="is_contactable" name="is_contactable" type="select" label={PROFILE_FIELD_LABELS.is_contactable}
                            value={form.is_contactable || ''} options={asOpts(IS_CONTACTABLE_OPTIONS)}
                            onChange={(e) => setField('is_contactable', e.target.value)} />
                        <div />
                        <InputField id="last_contacted_date" name="last_contacted_date" type="date" label={PROFILE_FIELD_LABELS.last_contacted_date}
                            value={form.last_contacted_date || ''} onChange={(e) => setField('last_contacted_date', e.target.value)} />
                        <InputField id="next_followup_date" name="next_followup_date" type="date" label={PROFILE_FIELD_LABELS.next_followup_date}
                            value={form.next_followup_date || ''} onChange={(e) => setField('next_followup_date', e.target.value)} />
                    </div>

                    <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Call / message log</h4>
                        <div className="flex gap-2 mb-3">
                            <select value={newLogChannel} onChange={(e) => setNewLogChannel(e.target.value)}
                                className="border border-gray-300 rounded-md px-2 text-sm">
                                <option value="call">Call</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="email">Email</option>
                                <option value="sms">SMS</option>
                                <option value="in-person">In person</option>
                            </select>
                            <input value={newLogNote} onChange={(e) => setNewLogNote(e.target.value)}
                                placeholder="Add a note…" className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm" />
                            <button onClick={addLogEntry} disabled={addingLog}
                                className="px-3 py-1.5 bg-brandColor text-white rounded-md text-sm font-medium flex items-center gap-1 disabled:opacity-50">
                                <Plus className="h-3.5 w-3.5" /> Add
                            </button>
                        </div>
                        <ul className="space-y-2 max-h-64 overflow-y-auto">
                            {commLog.length === 0 && <li className="text-sm text-gray-400">No log entries yet.</li>}
                            {commLog.map((entry) => (
                                <li key={entry.log_id} className="text-sm border-b pb-2">
                                    <span className="text-gray-400 mr-2">{new Date(entry.created_at).toLocaleString()}</span>
                                    <span className="font-medium text-gray-600 mr-1">{entry.channel || 'note'} ·</span>
                                    {entry.note}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {activeSection === 'qualification_status' && (
                <div className="space-y-5">
                    <div>
                        <InputField id="summary_notes" name="summary_notes" type="textarea" label={PROFILE_FIELD_LABELS.summary_notes} rows={4}
                            value={form.summary_notes || ''} onChange={(e) => setField('summary_notes', e.target.value)} />
                        {summaryLastUpdate && (
                            <p className="text-xs text-gray-400 mt-1">
                                Last updated{summaryLastUpdate.by ? ` by ${summaryLastUpdate.by}` : ''} on {new Date(summaryLastUpdate.at).toLocaleString()}
                            </p>
                        )}
                        {/* History is collapsed by default; the box above always holds the latest summary. */}
                        <div className="mt-3 border rounded-lg">
                            <button type="button" onClick={() => setSummaryHistoryOpen((v) => !v)}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                                <span>Lead Summary Notes history ({summaryHistory.length})</span>
                                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${summaryHistoryOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {summaryHistoryOpen && (
                                <div className="px-3 pb-3 border-t">
                                    {summaryHistory.length === 0 ? (
                                        <p className="text-sm text-gray-400 pt-2">No changes saved yet — each save will be listed here.</p>
                                    ) : (
                                        <ul className="space-y-2 max-h-64 overflow-y-auto pt-2">
                                            {summaryHistory.map((entry) => (
                                                <li key={entry.id} className="text-sm border-b pb-2 last:border-b-0">
                                                    <div className="text-xs text-gray-400 mb-0.5">
                                                        {new Date(entry.at).toLocaleString()}{entry.by ? ` · ${entry.by}` : ''}
                                                    </div>
                                                    <div className="text-gray-700 whitespace-pre-wrap">{entry.text || <span className="italic text-gray-400">(cleared)</span>}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <InputField id="counsellor_qualification" name="counsellor_qualification" type="select" label="Counsellor Qualification"
                            value={form.counsellor_qualification || ''} options={asOpts(COUNSELLOR_QUALIFICATION_OPTIONS)}
                            onChange={(e) => setField('counsellor_qualification', e.target.value)} />
                        <div />
                    </div>

                    {form.counsellor_qualification === 'Disqualified' && (
                        <div className="border border-red-200 bg-red-50 rounded-lg p-4 grid grid-cols-2 gap-3">
                            <InputField id="disqualified_reason" name="disqualified_reason" type="select" label="Disqualified Reason" required
                                value={form.disqualified_reason || ''} options={asOpts(DISQUALIFIED_REASONS)}
                                onChange={(e) => setField('disqualified_reason', e.target.value)} />
                            <div className="col-span-2">
                                <InputField id="disqualified_reason_notes" name="disqualified_reason_notes" type="textarea" label="Disqualified Reason Notes" required rows={2}
                                    value={form.disqualified_reason_notes || ''} onChange={(e) => setField('disqualified_reason_notes', e.target.value)} />
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <InputField id="overall_status" name="overall_status" type="select" label={PROFILE_FIELD_LABELS.overall_status}
                            value={form.overall_status || ''} options={asOpts(OVERALL_STATUS_OPTIONS)}
                            onChange={(e) => setField('overall_status', e.target.value)} />
                        <InputField id="counsellor_priority" name="counsellor_priority" type="select" label="Counsellor Priority"
                            value={form.counsellor_priority || ''} options={asOpts(COUNSELLOR_PRIORITY_OPTIONS)}
                            onChange={(e) => setField('counsellor_priority', e.target.value)} />
                    </div>

                    {form.overall_status === 'Closed/Dead' && (
                        <div className="border border-gray-300 bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3">
                            <InputField id="closed_dead_reason" name="closed_dead_reason" type="select" label="Closed/Dead Reason" required
                                value={form.closed_dead_reason || ''} options={asOpts(CLOSED_DEAD_REASONS)}
                                onChange={(e) => setField('closed_dead_reason', e.target.value)} />
                            <InputField id="competitor_school_joined" name="competitor_school_joined" type="text" label="Competitor School Joined" required
                                value={form.competitor_school_joined || ''} onChange={(e) => setField('competitor_school_joined', e.target.value)} />
                            <div className="col-span-2">
                                <InputField id="closed_dead_reason_notes" name="closed_dead_reason_notes" type="textarea" label="Closed/Dead Reason Notes" required rows={2}
                                    value={form.closed_dead_reason_notes || ''} onChange={(e) => setField('closed_dead_reason_notes', e.target.value)} />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
                            Parent Top 2 Considerations ({[form.consideration_1, form.consideration_2].filter(Boolean).length}/2 selected)
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {CONSIDERATION_OPTIONS.map((opt) => {
                                const selected = form.consideration_1 === opt || form.consideration_2 === opt;
                                return (
                                    <button key={opt} onClick={() => toggleConsideration(opt)}
                                        className={`px-3 py-1 rounded-full text-xs font-medium border ${
                                            selected ? 'bg-brandColor border-brandColor text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                                        }`}>
                                        {opt}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <InputField id="stage_of_admission" name="stage_of_admission" type="select" label="Stage of Admission"
                            value={form.stage_of_admission || ''} options={asOpts(STAGE_OF_ADMISSION_STEPS)}
                            onChange={(e) => setStageOfAdmission(e.target.value)} />
                        {/* Read-only: set by the server when Qualified Lead Status is saved as Admitted, cleared when it moves away */}
                        <InputField id="admission_date" name="admission_date" type="text" label="Admission Date" disabled
                            value={form.admission_date
                                ? new Date(form.admission_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                : ''}
                            onChange={() => {}} />
                    </div>
                </div>
            )}

            <Modal isOpen={stageNotices.length > 0} onClose={() => setStageNotices([])} size="md"
                title={stageNotices.includes('admitted') ? 'Student admitted' : 'Counsellor Qualification updated'}>
                <div className="p-4">
                    {stageNotices.includes('qualified') && (
                        <p className="text-sm text-gray-700 mb-4">
                            Since student reached or passed the <strong>Walk-in Planned</strong> stage of admission, <strong>Counsellor Qualification</strong> field has now been automatically set to <strong>‘Qualified’</strong>.
                        </p>
                    )}
                    {stageNotices.includes('admitted') && (
                        <p className="text-sm text-gray-700 mb-4">
                            Since student is admitted, <strong>Qualified Lead Status</strong> field has also been automatically set to <strong>‘Admitted’</strong>.
                        </p>
                    )}
                    <div className="flex justify-end mt-2">
                        <button onClick={() => setStageNotices([])}
                            className="px-4 py-2 bg-brandColor text-white rounded-md text-sm font-semibold">
                            OK
                        </button>
                    </div>
                </div>
            </Modal>

            {activeSection !== 'school_history' && activeSection !== 'references' && (
                // Pinned to the bottom of the scrolling form so Save is reachable on long tabs
                // (Qualification & Status) without scrolling past every field.
                <div className="sticky bottom-0 z-10 mt-6 py-3 border-t bg-white">
                    <button onClick={saveDetails} disabled={saving}
                        className="px-4 py-2 bg-brandColor text-white rounded-md text-sm font-semibold disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>
            )}
                </div>
            </div>
        </div>
    );
};

export default LeadProfileSections;
